import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { syncAppIconBadge, reportAppIconBadgeCapable } from '../services/appIconBadge';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe = null;

    const setupNotificationListener = (user) => {
      if (!user) {
        setNotifications([]);
        setUnreadCount(0);
        setLoading(false);
        return;
      }

      console.log('🔔 Setting up notification listener for user:', user.uid);
      console.log('🔔 Notification path:', `users/${user.uid}/notifications`);

      // Listen to user's notifications in real-time
      const notificationsRef = collection(db, 'users', user.uid, 'notifications');
      const q = query(notificationsRef); // Get all notifications, we'll sort by createdAt

      unsubscribe = onSnapshot(q, (snapshot) => {
        console.log('🔔 Firestore snapshot received, changes:', snapshot.docChanges().length);
        
        // Don't log every single change - too verbose, especially on initial load
        // Only log if there are NEW notifications (not initial load)
        if (snapshot.docChanges().length > 0 && snapshot.docChanges().length < 10) {
        snapshot.docChanges().forEach((change) => {
            const data = change.doc.data();
            console.log(`🔔 ${change.type}:`, change.doc.id, data.type);
        });
        }
        
        const notificationList = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          notificationList.push({
            id: doc.id,
            ...data
          });
        });

        // Sort by createdAt descending (newest first)
        notificationList.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });

        console.log('🔔 Notifications updated:', notificationList.length, 'notifications');
        setNotifications(notificationList);

        // Count unread notifications
        const unread = notificationList.filter(n => !n.read).length;
        setUnreadCount(unread);
        console.log('🔔 Unread count:', unread);

        setLoading(false);
      }, (error) => {
        console.error('❌ Error listening to notifications:', error);
        setLoading(false);
      });
    };

    // Set up auth listener
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (unsubscribe) {
        unsubscribe();
      }
      setupNotificationListener(user);
      // Native only, no-op on the web: let the server know this install can
      // show the icon badge, even if nothing is unread right now.
      if (user) reportAppIconBadgeCapable();
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      unsubscribeAuth();
    };
  }, []);

  // App icon badge (native only, no-op on the web): the home-screen number
  // follows the bell's unread count, including down to zero on sign-out.
  useEffect(() => {
    syncAppIconBadge(unreadCount);
  }, [unreadCount]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const notificationRef = doc(db, 'users', user.uid, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: new Date()
      });
      console.log('✅ Marked notification as read:', notificationId);
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      const updates = unreadNotifications.map(notification => {
        const notificationRef = doc(db, 'users', user.uid, 'notifications', notification.id);
        return updateDoc(notificationRef, {
          read: true,
          readAt: new Date()
        });
      });

      await Promise.all(updates);
      console.log('✅ Marked all notifications as read');
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
  };

  // Mark want-list-match notifications as read. Pass a `listingId` to clear only
  // the notification(s) for that one listing (used when the user opens that
  // listing's detail page); omit it to clear ALL want-list-match notifications
  // (used when the user lands on the Wantlist Matches page). Either action
  // decrements the Trading tab badge, since want_list_match is a Trading type.
  const markWantListMatchesAsRead = async (listingId = null) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // A price drop on a want-list pedal clears only when that listing is
      // opened (listingId given), not by visiting the matches page, which
      // doesn't show the drop.
      const targets = notifications.filter(n =>
        (n.type === 'want_list_match' || (listingId != null && n.type === 'want_list_price_drop')) && !n.read &&
        (listingId == null || n.metadata?.listingId === listingId)
      );
      if (targets.length === 0) return;
      const updates = targets.map(notification => {
        const notificationRef = doc(db, 'users', user.uid, 'notifications', notification.id);
        return updateDoc(notificationRef, {
          read: true,
          readAt: new Date()
        });
      });
      await Promise.all(updates);
      console.log(`✅ Marked ${targets.length} want_list_match notification(s) as read`, listingId ? `(listing ${listingId})` : '(all)');
    } catch (error) {
      console.error('❌ Error marking want_list_match notifications as read:', error);
    }
  };

  // Mark match notifications as read. `includeBatch` also clears the batched
  // "N new matches" alert, which only the Matches page itself should do — the
  // Trading hub calls this too, and the hub never lists the individual matches
  // that a batch alert is summarizing.
  const markMatchNotificationsAsRead = async ({ includeBatch = false } = {}) => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const matchNotifs = notifications.filter(
        n => (n.type === 'new_match' || (includeBatch && n.type === 'new_match_batch')) && !n.read
      );
      const updates = matchNotifs.map(notification => {
        const notificationRef = doc(db, 'users', user.uid, 'notifications', notification.id);
        return updateDoc(notificationRef, {
          read: true,
          readAt: new Date()
        });
      });

      await Promise.all(updates);
      console.log('✅ Marked all match notifications as read');
    } catch (error) {
      console.error('❌ Error marking match notifications as read:', error);
    }
  };

  // Every notification type that the bottom-bar Trading tab badge represents.
  // This is the full set of commerce activity — trade offers, purchase offers,
  // offer lifecycle, shipping/fulfillment, completions, and matches — so that on
  // the native app (where the top-nav bell is hidden on non-dashboard pages) all
  // of this is surfaced on the Trading tab. Anything NOT in this list is surfaced
  // on the Home tab via `otherNotificationCount` (messages, comments, welcome,
  // referral, early-bird, billing).
  //
  // The bar for membership here is that some Trading page can actually clear it.
  // `offer_sent`, `offers_sent_bulk`, and `offer_expired` are deliberately absent:
  // they are one-way notices with nothing to act on, so no page marks them read
  // and only the bell can dismiss them. On native the bell lives on Home alone,
  // so badging Trading for them just parks an uncleanable count on a tab that
  // can't do anything about it.
  const TRADING_NOTIFICATION_TYPES = [
    // Trade offers
    'new_offer', 'offer_status_change', 'offer_comment',
    // Purchase offers
    'new_purchase_offer', 'purchase_offer_status_change',
    // Offer lifecycle (trade or purchase)
    'offer_viewed',
    // Shipping / fulfillment / completion
    'ready_to_ship', 'tracking_added_trade', 'tracking_added_purchase',
    'package_delivered_trade', 'package_delivered_purchase',
    'trade_complete', 'purchase_complete',
    // 24h progress-step reminder
    'step_reminder',
    // Matches
    'new_match', 'new_match_batch',
    // Want-list matches (a listing matching an item on the user's Wantlist)
    // and price drops on those listings
    'want_list_match', 'want_list_price_drop',
  ];

  const unreadNotifications = notifications.filter(n => !n.read);

  // matchNotifications stays "new_match only" to preserve TradingHub's `newMatches` semantics.
  const matchNotifications = unreadNotifications.filter(n => n.type === 'new_match');
  // Trade bucket = all Trading types except plain matches (added separately in the
  // tab total) so the Trading tab badge (= trade + match counts) has no double-counting.
  const tradeNotifications = unreadNotifications.filter(n =>
    TRADING_NOTIFICATION_TYPES.includes(n.type) && n.type !== 'new_match'
  );
  // Home bucket = everything the Trading tab badge does NOT already cover.
  const otherNotifications = unreadNotifications.filter(n =>
    !TRADING_NOTIFICATION_TYPES.includes(n.type)
  );

  const value = {
    notifications,
    unreadCount,
    tradeNotificationCount: tradeNotifications.length,
    matchNotificationCount: matchNotifications.length,
    otherNotificationCount: otherNotifications.length, // Home tab badge (non-commerce notifications)
    newMatches: matchNotifications.length, // Add this for TradingHub
    loading,
    markAsRead,
    markAllAsRead,
    markMatchNotificationsAsRead,
    markWantListMatchesAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
